import VideoPlayer from './VideoPlayer.js';

class VimeoPlayer extends VideoPlayer {
  api_url = 'https://player.vimeo.com/api/player.js';
  script_id = 'vimeo-js-api';

  _apiLoaded() {
    return !!window.Vimeo;
  }

  _bindApi() {
    let iframe = document.getElementById(this.id);
    let player = (this.player = new window.Vimeo.Player(iframe));

    player.ready().then(() => {
      this.trigger(this.ready_event);
      window.player = player;

      this.setStart();

      // prevent vimeo autoplay unless explicitly enabled
      if (!this.opts.autoplay) {
        setTimeout(() => {
          this.player.pause();
        }, 0);
      }
    });

    player.on('ended', () => {
      this.trigger('stop');
      this.trigger('ended');
    });

    player.on('play', () => {
      console.log('[VimeoPlayer] Play event fired');
      this.trigger('play');
    });

    player.on('pause', () => {
      console.log('[VimeoPlayer] Pause event fired');
      this.trigger('pause');

      // It's a hacky way to close fullScreen on iOS
      player.getDuration().then((duration) => {
        if (
          this.opts.custom_range &&
          this.opts.end &&
          this.opts.end < duration &&
          this.opts.end <= Math.round(this.latestPlayTime)
        ) {
          this.player.exitFullscreen();
        }
      });
    });

    player.on('cuechange', () => {
      this.trigger('cue');
    });

    player.on('error', (event) => {
      this.trigger('error', event);
    });

    player.on('timeupdate', (seekTime) => {
      this.setLatestPlayTime(seekTime.seconds);
    });

    player.on('playbackratechange', (event) => {
      this.savePlaybackRate(event?.playbackRate || 1);
    });

    return this;
  }

  // Play video with sound unmuted
  async _playWithSound() {
    try {
      await this.player.setMuted(false);

      await this.player.setVolume(1);

      await this.player.play();
    } catch (error) {
      console.error('[VimeoPlayer] Error in _playWithSound:', error);
    }
  }

  _addEndCuePoint() {
    let player = this.player;

    player
      .addCuePoint(this.opts.end, {
        custom_end: true,
      })
      .then((cpId) => {
        this.custom_end_cuepoint = cpId;
      });

    player.on('cuepoint', (event) => {
      if (event.data.custom_end) {
        this.stop();
      }
    });
  }

  // Adjusting the playback rate is available only to members with a Vimeo Pro subscription or higher.
  // https://developer.vimeo.com/player/sdk/reference#methods-for-playback-controls
  _changePlaybackRate(speed = 1) {
    this.player.setPlaybackRate(speed);
  }

  // Note, this one is a promise, but VideoPlayer handles that
  _duration() {
    return this.player.getDuration();
  }

  // Playback controls
  // Top level VideoPlayer handles ready check
  // So can count on 'player' already being ready
  _play() {
    this.player.play();
  }

  _pause() {
    this.player.pause();
  }

  _seek(seconds) {
    this.player.setCurrentTime(seconds);
    //.then promise does not work above, using timeout instead
    setTimeout(() => {
      this.trigger('seek');
    }, 10);
  }

  _stop(terminal = false) {
    this._pause();
  }

  // [tk] - prevent duplicate call to test
  makeEmbeddable(url) {
    let matches = this.constructor.test(url);
    let id = matches[3];

    let params = new URLSearchParams({
      title: 0,
      byline: 0,
      portrait: 0,
      transparent: 0,
      share: 0,
    });

    if (matches[4]) {
      params.set('h', matches[4]);
    }

    // Enable autoplay when requested or when we have a start time (preload)
    // Desktop: allow Vimeo autoplay; Mobile: gesture gate handles it (no autoplay param)
    if (this.opts.autoplay || this.opts.start) {
      params.set('autoplay', '1');
    }

    return `https://player.vimeo.com/video/${id}?` + params.toString();
  }

  wireEvents() {
    this.on('opts', (newOpts, oldOpts) => {
      // Clear "started" so next play starts at custom start
      // if we have one
      this.started = false;

      if (newOpts.start != oldOpts.start) {
        this.seek(this.opts.start);
      }

      if (newOpts.end != oldOpts.end) {
        this._addEndCuePoint();
      }
    });

    if (this.opts?.autoplay && this.isMobile() && !this.gestureArmed) {
      this._armGestureAutoplay();
    }
  }

  // Helper method to wait for player to be ready (similar to YouTube/Wistia's ensurePlayer)
  _ensureReady(callback, attempts = 0) {
    const maxAttempts = 100; // 20 seconds max

    if (this.ready && this.player && typeof this.player.play === 'function') {
      return callback();
    }

    if (attempts >= maxAttempts) {
      console.warn('[Vimeo] Player ready timeout');
      return callback(); // Try anyway
    }

    setTimeout(() => {
      this._ensureReady(callback, attempts + 1);
    }, 200);
  }

  // Override base class method for Vimeo-specific autoplay logic
  async _handleGesturePlay() {
    // Wait for player to be ready (within gesture context)
    await new Promise((resolve) => {
      this._ensureReady(resolve);
    });

    // Now player is ready, play with sound
    await this._playWithSound();
  }
}

const vimeoRegex =
  /^(?:https?:)?(?:\/\/)?(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:\/(\w+))?(?:$|\/|\?)/;

VimeoPlayer.test = function (url) {
  return url.match(vimeoRegex);
};

export default VimeoPlayer;
